<script setup>
import apiClient from "@/apiClient.js";
import { computed, defineEmits, defineProps, ref, watch } from "vue";
const emits = defineEmits(["selected"]);
const props = defineProps({
  placeholder: { type: String, required: false, default: "Search contacts..." },
  value: { type: String, required: false, default: "" },
  autoShowResults: { type: Boolean, required: false, default: true },
  customerId: { type: [Number, String], required: false, default: null },
});
const createNew = ref(false);
const contact = ref({});
const query = ref(props.value ?? "");
const error = ref("");
const saving = ref(false);
const searchUrl = computed(() => {
  const customerId = Number(props.customerId);
  return Number.isFinite(customerId) && customerId > 0
    ? `/api/contact/search?customer_id=${customerId}&query=`
    : "/api/contact/search?query=";
});

const searchResultOutput = (contact) =>
  contact.name + " <" + contact.email + ">";
const selected = (val) => {
  emits("selected", val);
  query.value = val.name ? `${val.name} <${val.email}>` : "";
};

const noResults = (val) => {
  createNew.value = true;
  contact.value = { name: val };
  error.value = "";
};

const someResults = (val) => {
  createNew.value = false;
};

const save = async () => {
  saving.value = true;
  error.value = "";
  try {
    const response = await apiClient.post(
      "/api/contact",
      {
        ...contact.value,
        customer_id: props.customerId || null,
      },
      { suppressErrorAlert: true },
    );
    contact.value = response.data;
    selected(contact.value);
    createNew.value = false;
  } catch (saveError) {
    error.value =
      saveError.response?.data?.message ??
      "Unable to create this contact. Check the details and try again.";
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.value,
  (val) => {
    if (typeof val === "string" && val !== query.value) {
      query.value = val;
      createNew.value = false;
    }
  },
);
</script>
<template>
  <div>
    <div>
      <searchable-input
        class="my-4"
        v-model="query"
        :placeholder="props.placeholder"
        :url="searchUrl"
        :output="searchResultOutput"
        @selected="selected"
        @empty="noResults"
        @found="someResults"
        :canAdd="true"
        :addOnlyWhenEmpty="true"
        :clearOnSelect="false"
        :autoShowResults="props.autoShowResults"
      />
    </div>
    <form
      class="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4"
      v-if="createNew"
      @submit.prevent="save"
    >
      <label for="new-contact-name">Name</label>
      <input
        id="new-contact-name"
        type="text"
        v-model="contact.name"
        required
      />
      <label for="new-contact-email">Email</label>
      <input
        id="new-contact-email"
        type="email"
        v-model="contact.email"
        required
      />
      <label for="new-contact-phone">Phone Number</label>
      <input id="new-contact-phone" type="text" v-model="contact.phone" />
    </form>
    <p v-if="error" class="mt-2 text-sm text-rose-600" role="alert">
      {{ error }}
    </p>
    <button
      v-if="createNew"
      class="btn"
      :disabled="saving"
      @click.prevent="save"
    >
      {{ saving ? "Saving..." : "Save contact" }}
    </button>
    <button v-if="createNew" @click.prevent="createNew = false" class="btn">
      Cancel
    </button>
  </div>
</template>
